/**
 * RESPONSIBILITY: which sections appear on the page, and in what order.
 *
 * Composition only. No fetching beyond `cms.get`, no permissions, no side effects —
 * each <SectionUpdate> handles its own loading and its own pencil.
 *
 * Routing is app-level and needs nothing from the framework: `isScreen()` answers "is
 * this login or admin" and returns false for everything else, so anything left is a site
 * page and this decides which.
 *
 * THE SHARED LISTS ARE WRAPPED, NOT JUST READ. Events, sermons and the ministry cards
 * appear on several pages but get their pencil where they are SHOWN — the events list on
 * the events page, the sermons list on the sermons page, the cards on the home page.
 * Reading a section without wrapping it is how they ended up visible everywhere and
 * editable nowhere.
 */
import { useCallback } from 'react';
import { applyCssVars, useHash, useFlag } from '@framework/react';
import { SectionName } from '@app/sections';
import { cms, SectionUpdate } from './cms';
import { THEME_VARS } from './theme-vars';
import { FLAG_THEME_PENCIL } from './flags';
import {
  Header, Footer, Give, HouseOfHonor, Contact,
  WordsProvider,
  Page, routeFor, byNext, isVisible, isShown, bySlugOrId,
} from './site';
import { useScrollTop } from './useScrollTop';
import { useMapLinksInNewTab } from './useMapLinksInNewTab';
import {
  HomePage, AboutPage, EventsPage, EventPage, ChildrenPage, SermonsPage, LeadershipPage,
  MinistriesPage, VisitPage, BulletinPage, AdcPage, RequestsPage, MinistryOnePage,
} from './pages';

export function SitePage() {
  const route = routeFor(useHash());
  const showThemePencil = useFlag(FLAG_THEME_PENCIL);

  // Identifies the page being shown, not the scroll position within it. Used twice: to
  // put a new page at its top, and as the key that makes React remount the body so the
  // transition plays. Same string for both, because they answer the same question.
  const where = `${route.page}:${route.path.join('/')}`;
  useScrollTop(where);
  // Every map link on every page opens in a new tab — see the hook.
  useMapLinksInNewTab();

  // Live preview while the theme is being edited: applied straight to :root, so the
  // page recolours under the dialog without re-rendering anything.
  const previewTheme = useCallback(
    (d: Record<string, unknown>) => applyCssVars(THEME_VARS, d),
    [],
  );

  // One request — the loader batches every get() made in the same tick. These feed the
  // pages that only DISPLAY them; the page that owns a list wraps it. The appeal is here
  // for its PHOTOGRAPH, which the home page's giving band shows and does not own.
  const shared = cms.get([
    SectionName.Events, SectionName.Ministries, SectionName.HouseOfHonor,
  ]);

  // Somebody who may edit events sees them all, wherever they are listed — see when.ts.
  const mayEditEvents = shared.events.canWrite;
  const events = [...(shared.events.data?.events ?? [])]
    .filter((e) => mayEditEvents || isVisible(e))
    .sort(byNext);
  // And the same for ministries, for the same reason: a ministry switched off is gone
  // from Home and the index, but whoever switched it off still sees it there, marked.
  const mayEditMinistries = shared.ministries.canWrite;
  const ministries = (shared.ministries.data?.ministries ?? [])
    .filter((m) => mayEditMinistries || isShown(m));

  const footer = cms.get(SectionName.Footer);

  // Austin Desi Christmas stands alone: none of the church's header, menu or footer.
  if (route.page === Page.Adc) return <AdcPage />;

  return (
    /* Read once, here, and offered to the whole tree — the words the interface says are
       on every page, so no page owns them and no page should have to pass them on. The
       pencil travels with them: they appear in the breadcrumb bar, which belongs to no
       section, so there is nowhere else it could be. */
    <SectionUpdate name={SectionName.Words} pencil="none">
      {(w, editWords) => (
    <WordsProvider value={w as never} edit={editWords}>
      <SectionUpdate name={SectionName.Header}>
        {(c) => <Header c={c} overHero={route.page === Page.Home} />}
      </SectionUpdate>

      {/* Keyed on the route so React REMOUNTS on a page change rather than reconciling
          into the old tree. That is what lets one CSS animation read as a transition —
          without it the new page's markup appears inside the previous page's frame. */}
      <div className="page-swap" key={where}>

      {route.page === Page.Home && (
        <HomePage
          events={events} ministries={ministries}
          appealImage={shared.house_of_honor.data?.image}
        />
      )}

      {/* A single ministry is one ROW of the list, so its pencil opens that row. */}
      {route.page === Page.Ministry && !route.path[0] && <MinistriesPage />}

      {route.page === Page.Ministry && route.path[0] && (
        <MinistryOnePage slug={route.path[0]} events={events} />
      )}

      {route.page === Page.About && <AboutPage path={route.path} />}

      {route.page === Page.Children && <ChildrenPage events={events} />}

      {/* #events · #events/<tag> · #events/<tag>/<event>. The trail decides which:
          nothing is the whole page, one segment narrows it, two name an event.
          The event is found by its LAST segment alone, so re-tagging one changes its
          link without breaking the one already shared. */}
      {route.page === Page.Events && route.path.length < 2 && (
        <EventsPage tag={route.path[0]} />
      )}

      {route.page === Page.Events && route.path.length >= 2 && (
        <EventPage e={bySlugOrId(events, route.path[route.path.length - 1])} all={events} />
      )}

      {/* An event with no tag has no category to sit under, and every link shared
          before the category was in the URL is this shape too. */}
      {route.page === Page.Event && <EventPage e={bySlugOrId(events, route.path[0])} all={events} />}

      {route.page === Page.Sermons && <SermonsPage path={route.path} />}

      {route.page === Page.Bulletin && <BulletinPage />}

      {route.page === Page.Requests && <RequestsPage path={route.path} />}

      {route.page === Page.Leadership && <LeadershipPage />}

      {route.page === Page.Give && (
        <main>
          <SectionUpdate name={SectionName.Give}>
            {(c) => <Give c={c} />}
          </SectionUpdate>
        </main>
      )}

      {route.page === Page.Visit && <VisitPage />}

      {/* Its own page and its own section, not an alias of Give: the appeal makes a case,
          where Give answers how. */}
      {route.page === Page.HouseOfHonor && (
        <main>
          <SectionUpdate name={SectionName.HouseOfHonor}>
            {(c) => <HouseOfHonor c={c} />}
          </SectionUpdate>
        </main>
      )}

      {/* ONE section, one pencil. The email, phone and prayer address used to be read from
          the Footer, which never displays them, so the aside needed a second pencil that
          opened the whole footer form. They are the Contact section's now. */}
      {route.page === Page.Contact && (
        <SectionUpdate name={SectionName.Contact}>
          {(c) => <Contact c={c} />}
        </SectionUpdate>
      )}

      </div>

      <SectionUpdate name={SectionName.Footer}>
        {(c) => <Footer c={c} />}
      </SectionUpdate>

      {/* Behind a flag, and off by default: a chip over the corner of every page is
          editor chrome a visitor never needs, and even an admin only wants it on the
          day they are changing the palette. The toggle is in the account menu. */}
      {showThemePencil && (
        <div className="theme-dock">
          <SectionUpdate name={SectionName.Theme} preview={previewTheme}>
            {() => <span className="theme-chip">Theme</span>}
          </SectionUpdate>
        </div>
      )}
    </WordsProvider>
      )}
    </SectionUpdate>
  );
}
